import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { handleLogout } from "../services/logoutHandler";
import { refreshTokenAPI } from "./auth/authService";
import { useAuth } from "../hooks/useAuth";
import configAPI from "./config";
import { useSnackbar } from "../components/SnackBar/SnackbarContext";

const API_BASE_URL = configAPI.REACT_APP_API_BASE_URL || "http://localhost:3000";
// const API_BASE_URL = 'https://localhost:44335/';

let isRefreshing = false;
let refreshTokenSaved = [];

const successRefreshToken = (callback) => {
  refreshTokenSaved.push(callback);
};

const onTokenRefreshed = (newToken) => {
  refreshTokenSaved.forEach(callback => callback(newToken));
  refreshTokenSaved = [];
};

export const useVioletBaseFetch = () => {
  const { user } = useAuth();
  // const { showSnackbar } = useSnackbar();
  const token = useSelector(state => state.auth.token);

  const baseFetch = async (
    endpoint,
    {
      method = 'GET',
      body,
      headers = {},
      requiresAuth = false,
      withAuthHeader = false,
      retryCount = 0,
      maxRetries = 1,
    } = {}
  ) => {
    const url = `${API_BASE_URL}api/${endpoint}`;
    const isFormData = body instanceof FormData;

    const currentToken = token || localStorage.getItem('token') || '';

    const authHeaders =
      (requiresAuth || withAuthHeader) && currentToken
        ? { Authorization: `Bearer ${currentToken}` }
        : {};

    const finalHeaders = {
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders,
      ...headers,
    };

    const options = {
      method,
      headers: finalHeaders,
      ...(method !== 'GET' && body ? { body: isFormData ? body : JSON.stringify(body) } : {}),
    };

    try {
      const response = await fetch(url, options);
      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const responseData = isJson ? await response.json() : await response.text();
      
      if (response.status === 401 && retryCount < maxRetries) {
        if (isRefreshing) {
          return new Promise((resolve) => {
            successRefreshToken((newToken) => {
              resolve(baseFetch(endpoint, {
                method,
                body,
                headers,
                requiresAuth,
                withAuthHeader,
                retryCount: retryCount + 1,
                maxRetries,
              }));
            });
          });
        }

        isRefreshing = true;
        const refreshSuccess = await refreshAuthToken();
        
        if (refreshSuccess) {
          isRefreshing = false;
          onTokenRefreshed(refreshSuccess);
          return baseFetch(endpoint, {
            method,
            body,
            headers,
            requiresAuth,
            withAuthHeader,
            retryCount: retryCount + 1,
            maxRetries,
          });
        } else {
          isRefreshing = false;
          refreshTokenSaved = [];
        }
      }

      if (!response.ok) {
        let errorMessage = responseData?.message || '';

        switch (response.status) {
          case 400:
            errorMessage = errorMessage || 'Bad Request (400)';
            break;
          case 401:
            errorMessage = errorMessage || 'Unauthorized (401)';
            break;
          case 403:
            errorMessage = errorMessage || 'Forbidden (403)';
            break;
          case 404:
            errorMessage = errorMessage || 'Not Found (404)';
            break;
          case 500:
            errorMessage = errorMessage || 'Server Error (500)';
            break;
          default:
            errorMessage = errorMessage || `HTTP ${response.status}`;
            break;
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        return { error: true, message: error.message, status: error.status };
      }

      return responseData;
    } catch (err) {
      return { error: true, message: err.message || 'Unknown error' };
    }
  };

  const refreshAuthToken = async () => {
    try {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      const refPayload = {
        accessToken: token,
        refreshToken: refreshToken,
        roleName: user?.roleName,
      };
      const refreshResponse = await refreshTokenAPI(refPayload);
      if (refreshResponse?.success === false) {
        // handleLogout(dispatch, user, navigate, 'session-expired', showSnackbar);
        return false;
      } else {
        localStorage.setItem('token', refreshResponse.data.accessToken);
        localStorage.setItem('refreshToken', refreshResponse.data.refreshToken);
        return true;
      }
    } catch (refreshError) {
      // handleLogout(dispatch, user, navigate, 'session-expired', showSnackbar);
      return false;
    }
  };

  return baseFetch;
};